import React, { useCallback, useContext, useEffect, useState } from 'react'
import { PermissionsEnum } from '@/api/Identity/Types/PermissionsEnum'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import { hasPermission } from '@/utils/permissionsValidator'
import { SortDescriptor } from '@progress/kendo-data-query'
import { Grid, GridCellProps, GridColumn, GridSortChangeEvent } from '@progress/kendo-react-grid'
import { CreateLandingPage } from 'api/ClientArea/CreateLandingPage'
import { LandingPageDto } from 'api/ClientArea/LandingPageDto'
import { CreateFilter } from 'api/Identity/Types/CreateFilter'
import { PageData } from 'api/Pagination/Types'
import AddButton from 'components/AddButton/AddButton'
import { Pager } from 'components/components/Pager'
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal'
import { FilterType } from 'components/Filter'
import { SelectFiltersFacade } from 'components/Filter/ClientFields/SelectFiltersFacade'
import MoreButton from 'components/MoreButton/MoreButton'
import { Spinner } from 'components/Spinner'
import { successToast, ToastContext } from 'components/Toast/Toast'
import { createGridState, useGridFilter, useGridState } from 'features/DataGrid'
import { useGridStatusState } from 'features/DataGrid/hooks/useGridStatusState'
import { isGoBack } from 'lib/react-router-dom'
import { useAppDispatch, useAppSelector } from 'store'
import { fetchPromoMaterialsOptionsAction } from 'store/clientarea/clientarea.actions'
import { fetchClientLanguagesAction } from 'store/clients/clients.actions'
import {
  CompositeFilterDescriptor,
  GridFilterActionState,
  resetGridFilters,
  selectGridFilters,
  setGridFilters,
} from 'store/grid'
import {
  fetchFilterAction,
  fetchFilterTypesAction,
  removeFilterAction,
  saveFilterAction,
  updateFilterAction,
} from 'store/identity/identity.actions'
import { useApiClient } from 'utils/api/ApiClient'
import { ClientAreaApiClient } from 'utils/api/clientAreaApi'
import { ClientsApiClient } from 'utils/api/clientsApi'
import { IdentityApiClient } from 'utils/api/identityApi'
import { AuthSessionContext } from 'utils/AuthContext'
import { links } from 'utils/featureToggles'
import { isEmpty } from 'utils/is-empty'
import { buildQuery, buildSortQuery } from 'utils/QueryHelpers'
import { TableStateContext, TableStateContextData } from 'utils/TableStateContext'

import { GridColumn as GridColumnProps } from '../campaignsColumn'
import { ibLandingColumn } from './ibLandingColumn'
import { getIBLandingPageFilterFields } from './ibLandingFilter'
import { IbLandingForm } from './IbLandingForm'

const useMapStateToProps = () => ({
  languages: useAppSelector((state) => state.clients.language),
  tickmillCompanies: useAppSelector((state) => state.identity.tickmillCompanies),
  promoMaterialsOptions: useAppSelector((state) => state.clientArea.promoMaterialsOptions),
  filters: useAppSelector((state) => state.identity.filter),
  filterTypes: useAppSelector((state) => state.identity.filterType),
  gridState: useAppSelector((state) => state.grid),
  hasFilters: useAppSelector(selectGridFilters),
})

const useMapDispatchToProps = () => {
  const dispatch = useAppDispatch()
  const clientsApiClient = useApiClient(ClientsApiClient)
  const identityApiClient = useApiClient(IdentityApiClient)
  const clientAreaApiClient = useApiClient(ClientAreaApiClient)

  return {
    fetchFilterTypes: async () => dispatch(fetchFilterTypesAction(identityApiClient)),
    fetchFilters: async () => dispatch(fetchFilterAction(identityApiClient)),
    saveFilter: async (data: CreateFilter) => dispatch(saveFilterAction(data, identityApiClient)),
    updateFilter: async (filterId: string, data: CreateFilter) =>
      dispatch(updateFilterAction(filterId, data, identityApiClient)),
    removeFilter: async (id: string) => dispatch(removeFilterAction(id, identityApiClient)),
    setGridFilters: async (state: GridFilterActionState) => dispatch(setGridFilters(state)),
    resetGridFilters: async () => dispatch(resetGridFilters()),
    fetchClientLanguages: async () => dispatch(fetchClientLanguagesAction(clientsApiClient)),
    fetchPromoMaterialsOptions: async () =>
      dispatch(fetchPromoMaterialsOptionsAction(clientAreaApiClient)),
  }
}

const useIbLandingFetch = () => {
  const clientAreaApiClient = useApiClient(ClientAreaApiClient)

  const [tableData, setTableData] = useState<TableStateContextData>({ skipCount: true })
  const [campaigns, setCampaigns] = useState<LandingPageDto[]>([])

  const { gridStatusState, gridStatusActions } = useGridStatusState()
  const { gridState, gridActions } = useGridState()
  const { selectFilterForGoBackHistory } = useGridFilter(gridStatusActions)
  const { languages, tickmillCompanies, filters, filterTypes, promoMaterialsOptions } =
    useMapStateToProps()
  const { fetchFilterTypes, fetchFilters, fetchClientLanguages, fetchPromoMaterialsOptions } =
    useMapDispatchToProps()

  useEffect(() => {
    fetchIbLandingFetch()

    if (isEmpty(filterTypes)) {
      fetchFilterTypes()
    }
    if (isEmpty(filters)) {
      fetchFilters()
    }

    if (isEmpty(languages)) {
      fetchClientLanguages()
    }
    if (isEmpty(promoMaterialsOptions?.baseUrls)) {
      fetchPromoMaterialsOptions()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchIbLandingFetch = async () => {
    await fetchGrid(
      async (gridFilterState: GridFilterActionState): Promise<PageData<LandingPageDto[]>> => {
        const searchQuery = getQuery(gridFilterState.filter)
        const sortQuery = getSortQuery(gridFilterState.sort)
        const response = await clientAreaApiClient.getIBLandingPages({
          search: searchQuery,
          sort: sortQuery,
          pageIndex: gridFilterState.pageIndex,
          pageSize: gridFilterState.pageSize,
          skipCount: tableData?.skipCount !== false,
        })

        if (response?.items) {
          setCampaigns(response.items)
        }
        setTableData({ ...tableData, meta: response })

        return response
      }
    )
  }

  const fetchGrid = async (
    callback: (gridFilterState: GridFilterActionState) => Promise<PageData<LandingPageDto[]>>
  ) => {
    gridStatusActions.setLoading(true)

    const gridFilterState = selectFilterForGoBackHistory(
      createGridState({ skip: 0 }, gridState, { isGoBack })
    )

    const response = await callback(gridFilterState)
    if (response?.totalResults >= 0) {
      const total = response.totalResults

      gridActions.setGridFilters({
        total,
      })
    }

    gridStatusActions.setLoading(false)
  }

  const getQuery = useCallback(
    (filter?: CompositeFilterDescriptor) => {
      return buildQuery(filter ?? gridState.filter) ?? null
    },
    [gridState.filter]
  )

  const getSortQuery = useCallback(
    (sortDescriptors?: SortDescriptor[]): string | null => {
      return buildSortQuery(sortDescriptors ?? gridState.sort)
    },
    [gridState.sort]
  )

  const filterFetch = useCallback(
    async (params?: { query?: string | null }): Promise<void> => {
      const searchQuery = params?.query !== undefined ? params.query : getQuery()
      const sortQuery = getSortQuery(gridState.sort)
      const pageIndex = 1
      const pageSize = gridState.pageSize ?? 10
      const take = gridState.take ?? 10
      const skip = 0

      const response = await clientAreaApiClient.getIBLandingPages({
        search: searchQuery,
        sort: sortQuery,
        pageIndex,
        pageSize,
        skipCount: tableData?.skipCount !== false,
      })
      const total = response.totalResults

      if (response?.items) {
        setCampaigns(response.items.map((x) => Object.assign({ selected: false }, x)))
      }
      gridActions.setGridFilters({
        take,
        skip,
        total,
        pageIndex,
      })
      setTableData({ ...tableData, meta: response })
    },
    // tslint:disable-next-line:max-line-length
    [
      getQuery,
      getSortQuery,
      gridActions,
      gridState.pageSize,
      gridState.sort,
      gridState.take,
      tableData?.skipCount,
    ] // eslint-disable-line react-hooks/exhaustive-deps
  )

  return {
    fetchIbLandingFetch,
    promoMaterialsOptions,
    languages,
    tickmillCompanies,
    gridState,
    gridActions,
    filters,
    filterTypes,
    filterFetch,
    tableData,
    setTableData,
    campaigns,
    setCampaigns,
    setGridFilters: gridActions.setGridFilters,
    gridStatusState,
    gridStatusActions,
    getQuery,
    getSortQuery,
  }
}

const useIbLandingMutate = () => {
  const clientAreaApiClient = useApiClient(ClientAreaApiClient)
  const setToast = useContext(ToastContext)

  const handleAddSubmit = async (data: CreateLandingPage) => {
    await clientAreaApiClient.createLandingPage(data)
    setToast(successToast(`IB Landing Page added successfully.`))
  }

  const handleRemove = async (data: LandingPageDto) => {
    await clientAreaApiClient.deleteLandingPage(data.id)
    setToast(successToast(`IB Landing Page removed successfully.`))
  }

  return { handleAddSubmit, handleRemove }
}

const useIbLandingHandlers = (props: ReturnType<typeof useIbLandingFetch>) => {
  const { gridState, gridActions } = props
  const { getQuery, getSortQuery, setCampaigns, tableData, setTableData } = props

  const clientAreaApiClient = useApiClient(ClientAreaApiClient)

  const handleSkipCountChange = async (skipCount: boolean) => {
    props.gridStatusActions.setFetching(true)
    const searchQuery = getQuery()
    const sortQuery = getSortQuery(gridState.sort)

    const response = await clientAreaApiClient.getIBLandingPages({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: gridState.pageIndex,
      pageSize: gridState.take,
      skipCount,
    })
    const total = response.totalResults

    props.gridStatusActions.setFetching(false)

    if (response?.items) {
      setCampaigns(response.items)
    }
    gridActions.setGridFilters({
      total,
    })
    setTableData({ ...tableData, skipCount, meta: response })
  }

  const handleSortChange = async (event: GridSortChangeEvent) => {
    gridActions.setGridFilters({
      sort: event.sort,
    })

    props.gridStatusActions.setFetching(true)
    const searchQuery = getQuery(gridState.filter)
    const sortQuery = getSortQuery(event.sort)
    const response = await clientAreaApiClient.getIBLandingPages({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: gridState.pageIndex,
      pageSize: gridState.pageSize,
      skipCount: tableData?.skipCount !== false,
    })

    props.gridStatusActions.setFetching(false)

    if (response?.items) {
      setCampaigns(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  const handlePageSizeChange = async (pageSize: number) => {
    props.gridStatusActions.setFetching(true)
    await gridActions.setGridFilters({
      ...gridState,
      pageSize,
    })

    const searchQuery = getQuery()
    const sortQuery = getSortQuery(gridState.sort)

    const response = await clientAreaApiClient.getIBLandingPages({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: 1,
      pageSize,
      skipCount: tableData?.skipCount !== false,
    })

    props.gridStatusActions.setFetching(false)
    await gridActions.setGridFilters({
      ...gridState,
      pageIndex: 1,
      take: pageSize,
      total: response.totalResults,
    })
    if (response?.items) {
      setCampaigns(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  const handleGridPageChange = async (page: number) => {
    props.gridStatusActions.setFetching(true)
    const searchQuery = getQuery()
    const sortQuery = getSortQuery(gridState.sort)
    const pageIndex = page
    gridActions.setGridFilters({
      pageIndex,
    })

    const response = await clientAreaApiClient.getIBLandingPages({
      search: searchQuery,
      sort: sortQuery,
      pageIndex,
      pageSize: gridState.take,
      skipCount: tableData?.skipCount !== false,
    })
    const total = response.totalResults

    props.gridStatusActions.setFetching(false)

    gridActions.setGridFilters({
      total,
    })

    gridActions.setGridFilters({
      total,
    })
    if (response?.items) {
      setCampaigns(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  return {
    handleSkipCountChange,
    handleSortChange,
    handlePageSizeChange,
    handleGridPageChange,
    filterColumn,
  }
}

interface ModalProps {
  data: LandingPageDto | undefined
  visible: boolean
}

const initialModalState = { data: undefined, visible: false }

const useLandingModal = () => {
  const [landingModal, setLandingModal] = useState<ModalProps>(initialModalState)

  const handleLandingModalOpen = (data?: LandingPageDto) => {
    setLandingModal({ data, visible: true })
  }

  const handleLandingModalClose = () => {
    setLandingModal(initialModalState)
  }

  return { landingModal, handleLandingModalOpen, handleLandingModalClose }
}

const useConfirmationModal = () => {
  const [confirmationModal, setConfirmationModal] = useState<ModalProps>(initialModalState)

  const handleConfirmationModalOpen = (data?: LandingPageDto) => {
    if (data) {
      setConfirmationModal({ data, visible: true })
    }
  }

  const handleConfirmationModalClose = () => {
    setConfirmationModal(initialModalState)
  }

  return { confirmationModal, handleConfirmationModalOpen, handleConfirmationModalClose }
}

const IbLandingPage = () => {
  const crumbs = [{ title: 'Marketing - IB Landing Pages', link: links.ibLanding.url }]

  const landingFetch = useIbLandingFetch()
  const {
    gridState,
    gridStatusState,
    gridStatusActions,
    gridActions,
    languages,
    tickmillCompanies,
    promoMaterialsOptions,
    getQuery,
    filterFetch,
  } = landingFetch
  const { tableData, setTableData, campaigns } = landingFetch

  const { landingModal, handleLandingModalOpen, handleLandingModalClose } = useLandingModal()
  const { confirmationModal, handleConfirmationModalOpen, handleConfirmationModalClose } =
    useConfirmationModal()

  const handlers = useIbLandingHandlers(landingFetch)
  const mutate = useIbLandingMutate()
  const { handleSortChange, handleGridPageChange, handlePageSizeChange, handleSkipCountChange } =
    handlers

  const [auth] = useContext(AuthSessionContext)
  const permissions = auth?.permissions || []

  const handleSubmit = async (data: CreateLandingPage) => {
    await mutate.handleAddSubmit(data)
    await landingFetch.fetchIbLandingFetch()
    handleLandingModalClose()
  }

  const handleRemoveIbLanding = async (data?: LandingPageDto) => {
    if (data?.id) {
      await mutate.handleRemove(data)
      await landingFetch.fetchIbLandingFetch()
      handleConfirmationModalClose()
    }
  }

  if (gridStatusState.isLoading) {
    return <Spinner />
  }

  return (
    <React.Fragment>
      {confirmationModal.data && (
        <ConfirmationModal
          yesButtonText='Proceed'
          onYes={() => handleRemoveIbLanding(confirmationModal.data)}
          onCancel={handleConfirmationModalClose}
          visible={confirmationModal.visible}
          renderTitle={
            <div>
              <p>Are you sure you want to delete this IB landing page record?</p>
            </div>
          }
        />
      )}
      {landingModal.visible && (
        <IbLandingForm
          isOpen={landingModal.visible}
          onClose={handleLandingModalClose}
          data={landingModal.data}
          languages={languages.filter((l) => l.clientAreaVisible)}
          tickmillCompanies={tickmillCompanies}
          promoMaterialsOptions={promoMaterialsOptions}
          onSubmit={handleSubmit}
        />
      )}
      <Breadcrumbs crumbs={crumbs} />
      <SelectFiltersFacade
        gridState={gridState}
        gridStatusActions={gridStatusActions}
        gridStatusState={gridStatusState}
        setGridFilters={gridActions.setGridFilters}
        filterFetch={filterFetch}
        filterType={FilterType.MarketingIbLandingsOverview}
        filterFields={getIBLandingPageFilterFields()}
        getQuery={getQuery}
      />
      <AddButton
        text='Add Landing Page'
        onClick={handleLandingModalOpen}
        disabled={!hasPermission(permissions, PermissionsEnum.CanCreatePromoMaterial)}
      />
      <TableStateContext.Provider value={[tableData, setTableData]}>
        <Grid
          {...gridState}
          resizable
          data={campaigns}
          sort={gridState.sort}
          total={gridState.total}
          take={gridState.take}
          skip={gridState.skip}
          onSortChange={handleSortChange}
          pageable
          pager={() => (
            <Pager
              onSkipCountChange={handleSkipCountChange}
              onPageChange={handleGridPageChange}
              onPageSizeChange={handlePageSizeChange}
              page={gridState.pageIndex}
              pageSize={gridState.pageSize}
              take={gridState.take || 10}
              skip={gridState.skip || 0}
            />
          )}
          style={{ zIndex: 0 }}
        >
          {ibLandingColumn.filter(filterColumn).map((column, idx) => (
            <GridColumn key={idx + 2} {...column} />
          ))}
          <GridColumn
            width={50}
            title=''
            cell={(gridCellProps: GridCellProps) => (
              <td>
                <MoreButton
                  items={[
                    {
                      name: 'Delete',
                      onClick: () => handleConfirmationModalOpen(gridCellProps.dataItem),
                      disabled: !hasPermission(permissions, PermissionsEnum.CanDeletePromoMaterial),
                    },
                  ]}
                />
              </td>
            )}
          />
        </Grid>
      </TableStateContext.Provider>
    </React.Fragment>
  )
}

const filterColumn = (c: GridColumnProps) => {
  return c.show
}

export default IbLandingPage
